import React, { useState, useCallback, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * usePrompt — Hook para usar PromptDialog de forma imperativa.
 *
 * Reemplaza `window.prompt()` por un modal Shadcn con validación inline.
 *
 * Uso:
 *   const { prompt, dialogProps } = usePrompt();
 *   // ... en el JSX:
 *   <PromptDialog {...dialogProps} />
 *
 *   // en el handler:
 *   const value = await prompt({
 *     title: "Motivo del rechazo",
 *     label: "Explica brevemente el motivo (≥10 caracteres):",
 *     placeholder: "Pago duplicado, monto incorrecto, etc.",
 *     defaultValue: "",
 *     validator: (v) => v.trim().length >= 10 || "El motivo debe tener al menos 10 caracteres",
 *     testIdPrefix: "reject-reason",
 *     multiline: true,  // textarea en vez de input
 *   });
 *   if (value === null) return;  // user canceló
 *   // usar value
 */
export function usePrompt() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const resolverRef = useRef(null);

  const prompt = useCallback((options = {}) => {
    setOpts(options);
    setValue(options.defaultValue || "");
    setError(null);
    setSubmitting(false);
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (opts.validator) {
        const result = opts.validator(value);
        if (result !== true) {
          setError(typeof result === "string" ? result : "Valor inválido");
          setSubmitting(false);
          return;
        }
      }
      resolverRef.current?.(value);
      resolverRef.current = null;
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, [opts, value]);

  const handleOpenChange = useCallback((newOpen) => {
    if (!newOpen) {
      resolverRef.current?.(null);  // cancel → null
      resolverRef.current = null;
    }
    setOpen(newOpen);
  }, []);

  const dialogProps = {
    open,
    onOpenChange: handleOpenChange,
    title: opts.title || "Ingresa un valor",
    label: opts.label,
    description: opts.description,
    placeholder: opts.placeholder || "",
    confirmLabel: opts.confirmLabel || "Aceptar",
    cancelLabel: opts.cancelLabel || "Cancelar",
    multiline: !!opts.multiline,
    testIdPrefix: opts.testIdPrefix || "prompt",
    value,
    setValue,
    error,
    submitting,
    onAccept: handleAccept,
  };

  return { prompt, dialogProps };
}

/**
 * PromptDialog — Modal de input reutilizable.
 * Renderiza un input o textarea según `multiline`, con validación inline.
 */
export const PromptDialog = ({
  open, onOpenChange, title, label, description, placeholder,
  confirmLabel, cancelLabel, multiline, testIdPrefix,
  value, setValue, error, submitting, onAccept,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription className="pt-1 text-sm text-gray-600">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-2 py-2">
          {label && (
            <label className="block text-sm font-medium text-gray-700">
              {label}
            </label>
          )}
          {multiline ? (
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              rows={4}
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-burgundy"
              data-testid={`${testIdPrefix}-input`}
              autoFocus
              disabled={submitting}
            />
          ) : (
            <Input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              data-testid={`${testIdPrefix}-input`}
              autoFocus
              disabled={submitting}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAccept();
                }
              }}
            />
          )}
          {error && (
            <p className="text-xs text-red-600 mt-1" data-testid={`${testIdPrefix}-error`}>
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onAccept}
            disabled={submitting}
            className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            data-testid={`${testIdPrefix}-accept-btn`}
          >
            {submitting ? "Procesando…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default usePrompt;
